import Joi from "joi";
import { Text } from "@chakra-ui/react";

import { useAlertModal } from "modals/useAlertModal";
import RealEstateAgencyContactSendApiEndpoint from "utils/realEstateAgency/api/RealEstateAgencyContactSend";
import { useTracking } from "contexts/Tracking";
import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import { parsePhoneStringToObject } from "helpers/phoneUtils";

interface ISendValues {
  agency: string;
  software: string;
  name: string;
  email: string;
  phone: { number: string; prefix: string };
}

const FormSchema = Joi.object({
  agency: Joi.string().allow(null, ""),
  software: Joi.string().allow(null, ""),
  name: Joi.string().allow(null, ""),
  email: Joi.string().email({ tlds: false }).required(),
  approval: Joi.boolean().valid(true).required(),
  phone: JoiPhoneValidator(false),
});
interface IFormValues extends ISendValues {
  approval: boolean;
}

const CoopForm: React.FunctionComponent = () => {
  const { callEndpoint: sendContactApi } = useApiEndpoint(
    RealEstateAgencyContactSendApiEndpoint,
  );
  const { openModal: openAlertModal } = useAlertModal();
  const { trackEvent } = useTracking();
  const { user } = useGlobalState();

  const {
    registerWithError,
    handleSubmit,
    setValue,
    watch,
    errors,
    clearErrors,
  } = useForm<IFormValues>(FormSchema, {
    onLoginChange: (props) => {
      if (props.user) {
        props.form.setValue("email", props.user.email);
        props.form.setValue(
          "phone",
          parsePhoneStringToObject(props.user.phone),
        );
      }
    },
    defaultValues: {
      agency: "",
      software: "",
      name: "",
      email: user?.email || "",
      approval: true,
      phone: user?.renter?.phone || { number: "", prefix: "+420" },
    },
  });

  const phoneNumber = watch("phone.number");
  const phonePrefix = watch("phone.prefix");
  const emailWatch = watch("email");

  const handleData: OnFormSubmitType<IFormValues> = async (values) => {
    trackEvent({
      "dl.eCat": "RK",
      "dl.eAct": "odeslani_spoluprace",
      "dl.eLab": "pro_RK",
    });

    const { data } = await sendContactApi({
      ...values,
      phone: { prefix: phonePrefix, number: phoneNumber },
    });

    openAlertModal({
      animation: data ? "check" : "error",
      title: data ? "Úspěšně odesláno" : "Nastala chyba!",
      children: data
        ? "Naše obchodní oddělení se vám ozve co nejdříve."
        : "Pracujeme na jejím odstranění",
      submitText: "Děkuji",
    });
  };

  return (
    <form onSubmit={handleSubmit(handleData)}>
      <AnimatedInput
        {...registerWithError("agency")}
        type="text"
        label="Název kanceláře"
        autoComplete="organization"
        dataTest="rkform.agency"
      />
      <AnimatedInput
        {...registerWithError("software")}
        type="text"
        label="Název softwaru"
        dataTest="rkform.sotfware"
      />
      <AnimatedInput
        {...registerWithError("name")}
        type="text"
        label="Jméno"
        autoComplete="name"
        dataTest="rkform.name"
      />
      <AnimatedInput
        {...registerWithError("email")}
        type="email"
        label="Váš e-mail"
        autoComplete="email"
        required
        value={emailWatch}
        dataTest="rkform.email"
      />
      <PhoneInput
        backgroundColor="ud-white"
        error={errors.phone}
        setValue={setValue}
        selectBoxValue={phonePrefix}
        phoneNumberValue={phoneNumber}
        clearErrors={clearErrors}
      />
      <Button type="submit" w="100%" mb="10px" dataTest="rkform.submitButton">
        Vyzkoušet spolupráci
      </Button>
      <Text mt="5px" variant="small_2">
        Kliknutím souhlasíte s našimi{" "}
        <CustomLink showUnderline to="/zpracovani-osobnich-udaju">
          zásadami ochrany osobních údajů.
        </CustomLink>
      </Text>
    </form>
  );
};
export default CoopForm;
